import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, UserCheck } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Combobox } from '@/components/ui/Combobox';
import { toast } from '@/components/ui/Toast';
import { SectionSpinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/Table';
import { agentsApi, routesApi, shopsApi } from '@/services/endpoints/masterData';
import { queryKeys } from '@/services/queryKeys';
import { ApiError } from '@/services/apiClient';
import type { Route } from '@/domain/types';

/**
 * Both mappings from the requirements in one place: route-to-agent and
 * route-to-shops. They live together because they answer the same question —
 * "who collects from whom" — and splitting them across two screens makes it
 * easy to configure one and forget the other.
 */
export function RouteMappingModal({
  isOpen,
  onClose,
  route,
}: {
  isOpen: boolean;
  onClose: () => void;
  route: Route | null;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [shopToAdd, setShopToAdd] = useState<number | null>(null);

  const { data: shops, isLoading } = useQuery({
    queryKey: queryKeys.routes.shops(route?.id ?? 0),
    queryFn: () => routesApi.listShops(route!.id),
    enabled: isOpen && route !== null,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.routes.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.shops.all });
    // Invoices carry the live route and agent, so they change too.
    void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
  };

  const assignAgent = useMutation({
    mutationFn: (agentId: number | null) => routesApi.assignAgent(route!.id, agentId),
    onSuccess: () => {
      invalidate();
      toast.success(t('route.agentAssigned'));
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : t('errors.unknown')),
  });

  const addShop = useMutation({
    mutationFn: (shopId: number) => routesApi.assignShops(route!.id, [shopId]),
    onSuccess: (result) => {
      invalidate();
      setShopToAdd(null);
      toast.success(t('route.shopsAssigned', { count: result.assigned }));
      if (result.movedFromOtherRoutes > 0) {
        // A shop belongs to one route, so adding it here removed it elsewhere.
        // That is a side effect worth stating out loud.
        toast.info(t('route.shopsMoved', { count: result.movedFromOtherRoutes }));
      }
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : t('errors.unknown')),
  });

  const removeShop = useMutation({
    mutationFn: (shopId: number) => routesApi.removeShop(route!.id, shopId),
    onSuccess: () => {
      invalidate();
      toast.success(t('route.shopRemoved'));
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : t('errors.unknown')),
  });

  if (!route) return <></>;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={route.name}
      description={t('route.mappingHelp')}
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      <div className="space-y-6">
        {/* --- Route to agent ------------------------------------------- */}
        <section>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
            <UserCheck className="h-4 w-4 text-slate-400" />
            {t('route.assignAgent')}
          </h3>

          <Combobox
            value={route.agentId}
            initialLabel={route.agentName ?? ''}
            onChange={(agentId) => assignAgent.mutate(agentId)}
            onSearch={async (term) => {
              const results = await agentsApi.suggest(term);
              return results.map((agent) => ({
                id: agent.id,
                label: agent.name,
                sublabel: agent.code,
              }));
            }}
            placeholder={t('route.noAgent')}
          />
        </section>

        {/* --- Route to shops ------------------------------------------- */}
        <section>
          <h3 className="mb-2 text-sm font-semibold text-slate-900">
            {t('route.assignShops')}
            <span className="ml-2 text-xs font-normal text-slate-500">
              ({shops?.length ?? 0})
            </span>
          </h3>

          <Combobox
            value={shopToAdd}
            onChange={(shopId) => {
              setShopToAdd(shopId);
              if (shopId) addShop.mutate(shopId);
            }}
            onSearch={async (term) => {
              const results = await shopsApi.suggest(term);
              return results.map((shop) => ({
                id: shop.id,
                label: shop.name,
                sublabel: shop.routeName ?? t('shop.noRoute'),
              }));
            }}
            placeholder={t('common.searchPlaceholder')}
            clearable={false}
          />

          <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-slate-200">
            {isLoading ? (
              <SectionSpinner className="py-8" />
            ) : !shops || shops.length === 0 ? (
              <EmptyState title={t('common.noResults')} />
            ) : (
              <ul className="divide-y divide-slate-100">
                {shops.map((shop) => (
                  <li key={shop.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900">{shop.name}</p>
                      <p className="text-xs text-slate-500">
                        {shop.code}
                        {shop.contactNumber ? ` · ${shop.contactNumber}` : ''}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeShop.mutate(shop.id)}
                      title={t('route.removeShop')}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
}
